import { useEffect, useMemo, useState } from "react";
import AdminShell from "@/components/feature/AdminShell";
import { fetchOrgUsers, updateUserRole, ROLE_LABELS, type AdminUser, type UserRole } from "@/services/admin";
import { supabase } from "@/lib/supabaseClient";

const roleBadgeStyles: Record<UserRole, string> = {
  admin: "bg-primary-500/15 text-primary-700",
  socio: "bg-accent-500/15 text-accent-700",
  colaborador: "bg-background-200 text-foreground-700",
  prospecto: "bg-secondary-500/15 text-secondary-700",
};

export default function AdminUsuarios() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([fetchOrgUsers(), supabase.auth.getUser()])
      .then(([data, { data: userData }]) => {
        if (!active) return;
        setUsers(data);
        setCurrentUserId(userData.user?.id ?? null);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : "No se pudo cargar el equipo.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const handleRoleChange = async (user: AdminUser, role: UserRole) => {
    if (role === user.role) return;
    setSavingId(user.id);
    const previous = user.role;
    setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role } : u)));
    try {
      await updateUserRole(user.id, role);
      showToast(`${user.fullName ?? "Usuario"} ahora es ${ROLE_LABELS[role]}`);
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role: previous } : u)));
      showToast(err instanceof Error ? err.message : "No se pudo cambiar el rol.");
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.fullName ?? "").toLowerCase().includes(q));
  }, [users, query]);

  return (
    <AdminShell title="Usuarios" description="Equipo de tu organización. Cambia roles y permisos cuando lo necesites.">
      <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4 py-2.5 max-w-sm">
        <i className="ri-search-line text-foreground-400 text-sm"></i>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          type="text"
          placeholder="Buscar por nombre…"
          className="w-full bg-transparent outline-none text-sm text-foreground-950"
        />
      </div>

      {loading ? (
        <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
          <p className="mt-2">Cargando usuarios desde Supabase…</p>
        </div>
      ) : error ? (
        <div className="mt-6 bg-background-100 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">
          {error}
        </div>
      ) : (
        <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl overflow-hidden">
          <ul className="divide-y divide-background-200">
            {filtered.map((user) => (
              <li key={user.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="w-10 h-10 rounded-full bg-background-200 text-foreground-700 flex items-center justify-center flex-shrink-0 font-heading font-bold">
                    {(user.fullName ?? "?").charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-foreground-950 flex items-center gap-2">
                      {user.fullName ?? "Sin nombre"}
                      {user.id === currentUserId ? (
                        <span className="text-[10px] bg-background-200 text-foreground-600 px-2 py-0.5 rounded-full">Tú</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-foreground-500">
                      {user.sponsorName ? `Referido por ${user.sponsorName}` : "Sin referente"} · Desde{" "}
                      {new Date(user.createdAt).toLocaleDateString("es", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${roleBadgeStyles[user.role]}`}>
                    {ROLE_LABELS[user.role]}
                  </span>
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleChange(user, e.target.value as UserRole)}
                    disabled={savingId === user.id}
                    className="bg-background-50 border border-background-300 rounded-full px-3 py-1.5 text-xs font-semibold text-foreground-800 cursor-pointer outline-none disabled:opacity-50"
                  >
                    {(Object.keys(ROLE_LABELS) as UserRole[]).map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </option>
                    ))}
                  </select>
                  {savingId === user.id ? <i className="ri-loader-4-line animate-spin text-foreground-400"></i> : null}
                </div>
              </li>
            ))}
            {filtered.length === 0 ? (
              <li className="p-10 text-center text-foreground-500 text-sm">No hay usuarios que coincidan con tu búsqueda.</li>
            ) : null}
          </ul>
        </div>
      )}

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background-950 text-foreground-50 text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">
          {toast}
        </div>
      ) : null}
    </AdminShell>
  );
}
